import React from 'react'
import {Pressable, Text as RNText, View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {useSession} from '#/state/session'
import {
  atoms as a,
  useLayoutBreakpoints,
  useTheme,
  web,
} from '#/alf'
import * as Toggle from '#/components/forms/Toggle'
import {Text} from '#/components/Typography'
import {Divider} from '#/components/Divider'
import {InlineLinkText} from '#/components/Link'
import {useAutoScroll} from './AutoScrollContext'

export function VideosRightNav() {
  const t = useTheme()
  const {_} = useLingui()
  const {hasSession} = useSession()
  const {rightNavVisible} = useLayoutBreakpoints()
  const [isVisible, setIsVisible] = React.useState(true)
  const [legalOpen, setLegalOpen] = React.useState(false)
  const {isAutoScrolling, scrollSpeed, toggleAutoScroll, setScrollSpeed} =
    useAutoScroll()

  const handleFeedbackClick = () => {
    if (typeof window !== 'undefined' && (window as any).formbricks) {
      ;(window as any).formbricks.track('feedback')
    }
  }

  const toggleVisibility = () => {
    setIsVisible(!isVisible)
  }

  if (!rightNavVisible) {
    return null
  }

  // Always use 140px width for Videos right nav
  const width = 140

  return (
    <>
      {/* Toggle Button */}
      <Pressable
        onPress={toggleVisibility}
        style={[
          web({
            position: 'fixed',
            right: 10,
            top: 10,
            zIndex: 1000,
            backgroundColor: t.atoms.bg.backgroundColor,
            borderRadius: 8,
            padding: 8,
            cursor: 'pointer',
            border: `1px solid ${t.atoms.border_contrast_low.borderColor}`,
          }),
        ]}
        accessibilityLabel={isVisible ? 'Hide sidebar' : 'Show sidebar'}
        accessibilityRole="button">
        <Text style={[a.text_lg, t.atoms.text]}>
          {isVisible ? '✕' : '☰'}
        </Text>
      </Pressable>

      {/* Right Nav Content */}
      {isVisible && (
        <View
          style={[
            a.gap_lg,
            a.border_l,
            t.atoms.border_contrast_low,
            t.atoms.bg,
            web({
              position: 'fixed',
              right: 0,
              top: 0,
              width: width,
              maxHeight: '100%',
              overflowY: 'auto',
              paddingLeft: 16,
              paddingRight: 24,
              paddingTop: 20,
            }),
          ]}>
          <View style={[a.gap_md, a.pt_lg]}>
            <Text style={[a.text_lg, a.font_bold, t.atoms.text]}>
              {_(msg`Videos`)}
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {_(msg`Videos from your timeline`)}
            </Text>
          </View>

          <View style={[a.gap_sm]}>
            <Toggle.Item
              name="auto_scroll"
              label={_(msg`Auto-Scroll`)}
              value={isAutoScrolling}
              onChange={toggleAutoScroll}
              style={[a.w_full, a.justify_between]}>
              <Toggle.LabelText>{_(msg`Auto-Scroll`)}</Toggle.LabelText>
              <Toggle.Switch />
            </Toggle.Item>

            {isAutoScrolling && (
              <View
                style={[
                  a.flex_row,
                  a.align_center,
                  a.justify_between,
                  {flexWrap: 'wrap', gap: 4},
                ]}>
                {[0, 1, 2, 3, 4, 5].map(n => (
                  <Pressable
                    key={n}
                    onPress={() => setScrollSpeed(n)}
                    accessibilityLabel={`Speed ${n}`}
                    accessibilityRole="button"
                    style={[
                      a.align_center,
                      a.justify_center,
                      a.rounded_full,
                      web({cursor: 'pointer'}),
                      {
                        width: 28,
                        height: 28,
                        backgroundColor:
                          scrollSpeed === n
                            ? '#fd7c21'
                            : t.atoms.bg_contrast_50.backgroundColor,
                      },
                    ]}>
                    <Text
                      style={[
                        a.text_xs,
                        a.font_bold,
                        {
                          color:
                            scrollSpeed === n
                              ? '#fff'
                              : t.atoms.text.color,
                        },
                      ]}>
                      {String(n)}
                    </Text>
                  </Pressable>
                ))}
              </View>
            )}
          </View>

          <Divider />

          <View style={[a.gap_xs]}>
            {hasSession && (
              <Text
                onPress={handleFeedbackClick}
                style={[
                  {color: '#fd7c21'},
                  web({className: 'Feedback', cursor: 'pointer'}),
                ]}
                role="link">
                {_(msg`Feedback`)}
              </Text>
            )}
            <InlineLinkText to="/support/privacy" label={_(msg`Privacy`)}>
              {_(msg`Privacy`)}
            </InlineLinkText>
            <InlineLinkText label={_(msg`Help`)} to="/support">
              {_(msg`Help`)}
            </InlineLinkText>
            <RNText
              onPress={() => setLegalOpen(o => !o)}
              style={[{color: '#fd7c21'}, web({cursor: 'pointer'})]}
              role="button">
              {_(msg`Legal`)}
            </RNText>
            <InlineLinkText label={_(msg`About`)} to="/support/about">
              {_(msg`About`)}
            </InlineLinkText>
            {legalOpen && (
              <View style={[a.mt_xs, a.gap_xs, a.ml_md]}>
                <InlineLinkText
                  to="/support/community-guidelines"
                  label={_(msg`Community Guidelines`)}>
                  {_(msg`Community Guidelines`)}
                </InlineLinkText>
                <InlineLinkText
                  to="/support/tos"
                  label={_(msg`Terms of Service`)}>
                  {_(msg`Terms of Service`)}
                </InlineLinkText>
                <InlineLinkText
                  to="/support/privacy-policy"
                  label={_(msg`Privacy Policy`)}>
                  {_(msg`Privacy Policy`)}
                </InlineLinkText>
                <InlineLinkText
                  to="/support/copyright"
                  label={_(msg`Copyright Policy`)}>
                  {_(msg`Copyright Policy`)}
                </InlineLinkText>
                <InlineLinkText
                  to="/support/2257"
                  label={_(msg`2257 Disclosure`)}>
                  {_(msg`2257 Disclosure`)}
                </InlineLinkText>
              </View>
            )}
          </View>
        </View>
      )}
    </>
  )
}
